import {
  Component,
  DestroyRef,
  EventEmitter,
  Input,
  OnInit,
  Output,
} from "@angular/core";
import { CommonModule } from "@angular/common";
import { ActivatedRoute } from "@angular/router";
import { TableLazyLoadEvent, TableModule } from "primeng/table";
import { CardModule } from "primeng/card";
import { ButtonModule } from "primeng/button";
import { InputTextModule } from "primeng/inputtext";
import { ToggleSwitchModule } from "primeng/toggleswitch";
import { SkeletonModule } from "primeng/skeleton";
import { FormControl, ReactiveFormsModule } from "@angular/forms";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { debounceTime, Subject } from "rxjs";
import { CreateGroupModalComponent } from "./create-group-modal/create-group-modal.component";
import { UpdateGroupModalComponent } from "./update-group-modal/update-group-modal.component";

import { ClientGroupService } from "../../../core/services/client-group.service";
import { JniToastrService } from "../../../core/services/jni-toastr.service";
import { GenericFilterRequestDto } from "../../../core/models/generic-filter-request.dto";
import { GenericSortRequestDto } from "../../../core/models/generic-sort-request.dto";
import { GroupUserDto } from "../../../core/models/client-group/group-user.dto";
import { SpinnerComponent } from "../../../shared/components/spinner/spinner.component";
import { ConfirmDialogComponent } from "../../../shared/components/confirm-dialog/confirm-dialog.component";

export interface GroupDetail {
  groupId: number;
  groupName: string;
  description: string | null;
  userCount: number;
  createdDate: string;
  isActive: boolean;
  users: GroupUserDto[];
  enabledControl: FormControl<boolean>;
}

@Component({
  selector: "jni-client-group-detail",
  standalone: true,
  imports: [
    CommonModule,
    TableModule,
    CardModule,
    ButtonModule,
    InputTextModule,
    ToggleSwitchModule,
    SkeletonModule,
    ReactiveFormsModule,
    CreateGroupModalComponent,
    UpdateGroupModalComponent,
    SpinnerComponent,
    ConfirmDialogComponent,
  ],
  templateUrl: "./client-group-detail.component.html",
  styleUrls: ["./client-group-detail.component.scss"],
})
export class ClientGroupDetailComponent implements OnInit {
  /** Optional client id supplied when the detail is rendered inside the stacker. */
  @Input() clientIdInput: string | null = null;
  /** Optional client name supplied when the detail is rendered inside the stacker. */
  @Input() clientNameInput = "";
  /** Emitted when the close (X) button is clicked while rendered inside the stacker. */
  @Output() closeModalEvent = new EventEmitter<void>();

  clientId = "";
  clientName = "";
  groupDetails: GroupDetail[] = [];
  totalRecords = 0;
  loading = true;
  rows = 50;
  showCreateModal = false;
  showUpdateModal = false;
  selectedGroup: GroupDetail | null = null;
  showDisableConfirm = false;
  disableConfirmMessage = "";
  private _pendingDisableGroup: GroupDetail | null = null;
  private _lastLoadEvent: TableLazyLoadEvent = { first: 0, rows: this.rows };

  filterTextChanged: Subject<TableLazyLoadEvent> =
    new Subject<TableLazyLoadEvent>();
  filterDebounceTime = 500;
  private lastFiltersJson = "";
  private isInitialLoad = true;

  constructor(
    private _clientGroupService: ClientGroupService,
    private _toastr: JniToastrService,
    private _route: ActivatedRoute,
    private _destroyRef: DestroyRef
  ) {}

  ngOnInit(): void {
    this.filterTextChanged
      .pipe(
        debounceTime(this.filterDebounceTime),
        takeUntilDestroyed(this._destroyRef)
      )
      .subscribe(event => this.callApi(event));

    if (this.clientIdInput) {
      // Rendered inside the stacker: load directly from the provided inputs.
      this.clientId = this.clientIdInput;
      this.clientName = this.clientNameInput || this.clientId;
      this.isInitialLoad = true;
      this.lastFiltersJson = "";
      this.loadGroups({ first: 0, rows: this.rows });
      return;
    }

    this._route.params
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe(params => {
        this.clientId = params["clientId"] || "";
        this.clientName = history.state?.clientName || this.clientId;
        this.isInitialLoad = true;
        this.lastFiltersJson = "";
        this.loadGroups({ first: 0, rows: this.rows });
      });
  }

  loadGroups(event: TableLazyLoadEvent): void {
    this.loading = true;
    this._lastLoadEvent = event;

    const filtersJson = JSON.stringify(event.filters ?? {});
    const isFilterChange =
      filtersJson !== this.lastFiltersJson && !this.isInitialLoad;
    this.lastFiltersJson = filtersJson;
    this.isInitialLoad = false;

    if (isFilterChange) {
      this.filterTextChanged.next(event);
    } else {
      this.callApi(event);
    }
  }

  private callApi(event: TableLazyLoadEvent): void {
    this.loading = true;

    const skip = event.first ?? 0;
    const take = event.rows ?? this.rows;

    const filters: GenericFilterRequestDto[] = [];
    if (event.filters) {
      for (const [key, filterMeta] of Object.entries(event.filters)) {
        const meta = Array.isArray(filterMeta) ? filterMeta[0] : filterMeta;
        if (meta?.value) {
          filters.push({
            columnName: key,
            value: String(meta.value),
            matchMode: meta.matchMode ?? "contains",
            tableName: "",
          });
        }
      }
    }

    const sort: GenericSortRequestDto[] = [];
    if (event.sortField) {
      sort.push({
        field: event.sortField as string,
        order: event.sortOrder ?? 0,
      });
    }

    this._clientGroupService
      .getGroupsByClient(this.clientId, skip, take, filters, sort)
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe({
        next: response => {
          this.groupDetails = response.data.map(group => ({
            ...group,
            enabledControl: new FormControl(group.isEnabled, {
              nonNullable: true,
            }),
          }));
          this.totalRecords = response.totalRecords ?? 0;
          this.loading = false;
        },
        error: () => {
          this.loading = false;
        },
      });
  }

  openCreateModal(): void {
    this.showCreateModal = true;
  }

  onCloseModalEvent(): void {
    this.closeModalEvent.emit();
  }

  onGroupCreated(): void {
    this.showCreateModal = false;
    this.loadGroups(this._lastLoadEvent);
  }

  openUpdateModal(group: GroupDetail): void {
    this.selectedGroup = group;
    this.showUpdateModal = true;
  }

  closeUpdateModal(): void {
    this.showUpdateModal = false;
    this.selectedGroup = null;
  }

  onToggleChange(item: GroupDetail, checked: boolean): void {
    // Enabling a group never has side effects, so apply it directly.
    if (checked) {
      this._setGroupStatus(item, true);
      return;
    }

    // Disabling delinks the group from every project it is associated with, so check
    // for associations first and warn the user before proceeding.
    this._clientGroupService
      .getGroupProjectAssociation(item.groupId)
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe({
        next: association => {
          if (association.isAssociated) {
            this._confirmDisable(item, association.projectCount);
          } else {
            this._setGroupStatus(item, false);
          }
        },
        error: () => {
          item.enabledControl.setValue(true, { emitEvent: false });
          this._toastr.error(
            "Unable to verify project associations. Please try again."
          );
        },
      });
  }

  onGroupUpdated(): void {
    this.showUpdateModal = false;
    this.selectedGroup = null;
    this.loadGroups(this._lastLoadEvent);
  }

  /**
   * Prompts the user to confirm disabling a group that is linked to one or more projects.
   * Disabling is applied on confirmation; the toggle is reverted if the user declines.
   * @param item The group being disabled.
   * @param projectCount The number of projects the group is associated with.
   */
  private _confirmDisable(item: GroupDetail, projectCount: number): void {
    const projectLabel = projectCount === 1 ? "project" : "projects";
    const demonstrative = projectCount === 1 ? "that" : "those";
    this._pendingDisableGroup = item;
    this.disableConfirmMessage = `This group is associated with ${projectCount} ${projectLabel}. Disabling it will remove its access from ${demonstrative} ${projectLabel}. Do you want to disable this group?`;
    this.showDisableConfirm = true;
  }

  /**
   * Handles the user's response to the disable-group confirmation. Applies the disable on
   * confirmation; reverts the toggle when declined.
   * @param confirmed `true` when the user confirmed the disable; otherwise, `false`.
   */
  onDisableConfirmStatus(confirmed: boolean): void {
    const item = this._pendingDisableGroup;
    this.showDisableConfirm = false;
    this._pendingDisableGroup = null;

    if (!item) {
      return;
    }

    if (confirmed) {
      this._setGroupStatus(item, false);
    } else {
      item.enabledControl.setValue(true, { emitEvent: false });
    }
  }

  /**
   * Persists the group's active status and reverts the toggle when the update fails.
   * @param item The group whose status is changing.
   * @param isActive `true` to enable the group; otherwise, `false`.
   */
  private _setGroupStatus(item: GroupDetail, isActive: boolean): void {
    this._clientGroupService
      .updateGroupStatus(this.clientId, item.groupId, isActive)
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe({
        next: () => {
          item.isActive = isActive;
          this._toastr.success(
            isActive
              ? "Group is activated successfully"
              : "Group is deactivated successfully"
          );
        },
        error: () => {
          item.enabledControl.setValue(!isActive, { emitEvent: false });
          this._toastr.error("Unable to update status. Please try again.");
        },
      });
  }
}
